import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../../components/auth/index.js';
import { useEngineerEvaluations } from '../../hooks/useEngineerEvaluation.js';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';
import type { EngineerEvaluationSummary } from '../../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニアスキル評価一覧ページ。四半期フィルタでスコア・positive/negative件数のサマリーを表示
 * @graph-connects useEngineerEvaluations [calls] エンジニア評価一覧フック呼び出し
 */
export const Route = createFileRoute('/engineer-evaluations/')({
  component: EngineerEvaluationsGuard,
});

/**
 * 一覧は他メンバーの評価も含むため VIEW_ENGINEER_EVALUATION 権限を要求する
 * @graph-connects ProtectedRoute [calls] 権限チェック
 */
function EngineerEvaluationsGuard() {
  return (
    <ProtectedRoute pageKey="engineerEvaluation">
      <EngineerEvaluationsPage />
    </ProtectedRoute>
  );
}

/**
 * スコアに応じた色を返す
 * @graph-connects none
 */
function getScoreColor(score: number | null): string {
  if (score == null) return 'var(--color-text-muted)';
  if (score >= 7.0) return 'var(--color-success)';
  if (score >= 5.0) return 'var(--color-info)';
  if (score >= 3.0) return 'var(--color-warning)';
  return 'var(--color-error)';
}

/**
 * レベルバッジ
 * @graph-connects none
 */
function getLevelBadge(level: string) {
  switch (level) {
    case 'Tech Lead':
      return <Badge variant="success">{level}</Badge>;
    case 'Middle':
      return <Badge variant="info">{level}</Badge>;
    case 'Junior':
      return <Badge variant="default">{level}</Badge>;
    default:
      return <Badge>{level}</Badge>;
  }
}

/** @graph-connects none */
function EngineerEvaluationsPage() {
  const navigate = useNavigate();
  const [selectedQuarter, setSelectedQuarter] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, refetch } = useEngineerEvaluations({
    quarter: selectedQuarter,
  });

  const quarterOptions = (data?.quarters ?? []).map((q) => ({ value: q, label: q }));

  const evaluations = data?.evaluations ?? [];
  const totalEngineers = new Set(evaluations.map((e) => e.email)).size;
  const scored = evaluations.filter((e) => e.averageScore != null);
  const avgScore =
    scored.length > 0
      ? (scored.reduce((sum, e) => sum + (e.averageScore ?? 0), 0) / scored.length).toFixed(1)
      : '-';

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Engineer Skill Evaluation
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">エンジニアのスキル評価結果一覧</p>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Engineer Skill Evaluation
          </h1>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Engineer Skill Evaluation
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">エンジニアのスキル評価結果一覧</p>
        </div>
        <div className="flex gap-2">
          {quarterOptions.length > 0 && (
            <Select
              options={quarterOptions}
              value={selectedQuarter ?? data?.quarters[0] ?? ''}
              onChange={(e) => {
                setSelectedQuarter(e.target.value);
              }}
              aria-label="四半期選択"
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">対象エンジニア</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">
            {String(totalEngineers)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">評価件数</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">
            {String(evaluations.length)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">平均スコア</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">{avgScore}</p>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader title="評価一覧" />
        <CardContent>
          {evaluations.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-muted)]">評価データなし</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      Engineer
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      Level
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      Team
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {evaluations.map((ev: EngineerEvaluationSummary) => (
                    <tr
                      key={ev.email}
                      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer"
                      onClick={() => {
                        void navigate({
                          to: '/engineer-evaluations/$email',
                          params: { email: ev.email },
                          search: { quarter: ev.quarter },
                        });
                      }}
                    >
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">
                          {ev.nickname}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">{ev.email}</p>
                      </td>
                      <td className="py-3 px-4 text-center">{getLevelBadge(ev.level)}</td>
                      <td className="py-3 px-4 text-center text-sm text-[var(--color-text-secondary)]">
                        {ev.teamName}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className="text-lg font-semibold"
                          style={{ color: getScoreColor(ev.averageScore) }}
                        >
                          {ev.averageScore != null ? ev.averageScore.toFixed(1) : '-'}
                        </span>
                        <span className="text-xs text-[var(--color-text-muted)] ml-2">
                          +{String(ev.positiveCount)} -{String(ev.negativeCount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
